/**
 * CTF Scoreboard UI Component
 * Displays team scores and capture progress for Capture The Flag mode
 */

import { useEffect, useState } from "react";
import { uiBridge } from "./ui.bridge";

interface CTFScoreboardData {
  teamScores: Record<number, number>;
  captureLimit: number;
  flags: Array<{
    id: string;
    teamId: number;
    state: "home" | "carried" | "dropped";
    carrierId?: string;
  }>;
}

/**
 * CTF Scoreboard Component
 * Only visible when in CTF mode with active game state
 */
export function CTFScoreboardComponent() {
  const [ctfData, setCTFData] = useState<CTFScoreboardData | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const gameModeState = uiBridge.getGameModeState?.();
      const started = uiBridge.isGameStarted?.();

      if (started && gameModeState?.mode === "ctf") {
        setCTFData({
          teamScores: gameModeState.teamScores || { 0: 0, 1: 0 },
          captureLimit: gameModeState.captureLimit || 3,
          flags: gameModeState.flags || [],
        });
        setIsVisible(true);
      } else {
        setIsVisible(false);
        setCTFData(null);
      }
    }, 50);

    return () => clearInterval(interval);
  }, []);

  if (!isVisible || !ctfData) {
    return null;
  }

  const team0Score = ctfData.teamScores[0] || 0;
  const team1Score = ctfData.teamScores[1] || 0;
  const isTeam0Leading = team0Score > team1Score;
  const isTeam1Leading = team1Score > team0Score;

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>

      <div
        style={{
          position: "fixed",
          top: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: "30px",
          padding: "15px 30px",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          border: "2px solid #00ff00",
          borderRadius: "4px",
          fontFamily: "'Space Mono', monospace",
          fontSize: "18px",
          fontWeight: "bold",
          color: "#00ff00",
          zIndex: 1000,
          textTransform: "uppercase",
          letterSpacing: "2px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            color: isTeam0Leading ? "#61afef" : "#6699cc",
            filter: isTeam0Leading ? "drop-shadow(0 0 8px #61afef)" : "none",
          }}
        >
          <span style={{ fontSize: "14px" }}>BLUE</span>
          <span style={{ fontSize: "28px", fontWeight: "bold" }}>
            {team0Score}
          </span>
        </div>

        <div
          style={{
            color: "#00ff00",
            fontSize: "12px",
            padding: "0 15px",
            borderLeft: "1px solid #00ff00",
            borderRight: "1px solid #00ff00",
          }}
        >
          FIRST TO {ctfData.captureLimit}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            color: isTeam1Leading ? "#e06c75" : "#ff6666",
            filter: isTeam1Leading ? "drop-shadow(0 0 8px #e06c75)" : "none",
          }}
        >
          <span style={{ fontSize: "28px", fontWeight: "bold" }}>
            {team1Score}
          </span>
          <span style={{ fontSize: "14px" }}>RED</span>
        </div>
      </div>

      {/* Flag Status Indicators */}
      <div
        style={{
          position: "fixed",
          top: "100px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        {ctfData.flags
          .filter((flag) => flag.state === "carried")
          .map((flag) => {
            const flagColor = flag.teamId === 0 ? "#61afef" : "#e06c75";
            const flagTeam = flag.teamId === 0 ? "BLUE" : "RED";

            return (
              <div
                key={flag.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 16px",
                  backgroundColor: "rgba(0, 0, 0, 0.85)",
                  border: `2px solid ${flagColor}`,
                  borderRadius: "4px",
                  fontFamily: "'Space Mono', monospace",
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "#00ff00",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  boxShadow: `0 0 10px ${flagColor}`,
                  animation: "pulse 2s infinite",
                }}
              >
                <span style={{ color: flagColor }}>🚩 {flagTeam} FLAG</span>
                <span style={{ color: "#ffffff" }}>BEING CARRIED</span>
              </div>
            );
          })}
      </div>
    </>
  );
}
